import { CreatePermissionInput, EditPermissionInput } from 'schema'
import { LabeledValueType } from 'components/CIMSSelect/types'
import {
  getPermissionActionMetadata,
  isWorkflowPermissionAction,
  WorkflowPermissionAction,
} from './metadata'
import {
  NormalizedPermissionConditions,
  PermissionFormValues,
  PermissionMutationConditions,
  WorkflowPermissionField,
  WorkflowPermissionRecord,
  WorkflowPermissionStatus,
} from './types'

type SelectableRecord = WorkflowPermissionField | WorkflowPermissionStatus

export const CLOSED_STATUS_ID = 'closed' as const

export const CLOSED_STATUS_LABEL = 'Closed'

export const DRAFT_STATUS_ID = 'draft' as const

export const DRAFT_STATUS_LABEL = 'Draft'

/**
 * Converts stored condition IDs to select values while preserving missing or
 * deleted records for administrators to review.
 */
export function toSelectableLabeledValues<T extends SelectableRecord>(
  ids: string[] | undefined,
  records: Map<string, T>,
  type: 'field' | 'status'
): LabeledValueType[] {
  return (ids ?? []).map((id) => {
    const record = records.get(id)
    if (record) {
      return {
        value: record.id,
        label: record.name,
        tooltip: record.tooltip,
        deleted: record.deleted,
      }
    }
    if (type === 'status' && id === CLOSED_STATUS_ID) {
      return {
        value: CLOSED_STATUS_ID,
        label: CLOSED_STATUS_LABEL,
        tooltip: 'System status',
      }
    }
    if (type === 'status' && id === DRAFT_STATUS_ID) {
      return {
        value: DRAFT_STATUS_ID,
        label: DRAFT_STATUS_LABEL,
        tooltip: 'System status',
      }
    }
    return {
      value: id,
      label: `Unknown ${type} (${id})`,
      tooltip: `This ${type} could not be found.`,
      deleted: true,
    }
  })
}

export function normalizePermissionConditions(
  conditions: unknown
): NormalizedPermissionConditions {
  if (
    typeof conditions !== 'object' ||
    conditions === null ||
    Array.isArray(conditions)
  ) {
    return { fieldIds: [], statusIds: [] }
  }
  const record = conditions as Record<string, unknown>
  return {
    approvalStatusId:
      typeof record.approvalStatusId === 'string'
        ? record.approvalStatusId
        : undefined,
    fieldIds: Array.isArray(record.fieldIds)
      ? record.fieldIds.filter(
          (item): item is string => typeof item === 'string'
        )
      : [],
    statusIds: Array.isArray(record.statusIds)
      ? record.statusIds.filter(
          (item): item is string => typeof item === 'string'
        )
      : [],
  }
}

export function permissionConditionName<T extends SelectableRecord>(
  id: string,
  records: Map<string, T>,
  type: 'field' | 'status'
): string {
  if (type === 'status' && id === CLOSED_STATUS_ID) return CLOSED_STATUS_LABEL
  if (type === 'status' && id === DRAFT_STATUS_ID) return DRAFT_STATUS_LABEL
  return records.get(id)?.name ?? `Unknown ${type} (${id})`
}

export function permissionConditionSearchText(
  record: WorkflowPermissionRecord,
  fields: Map<string, WorkflowPermissionField>,
  statuses: Map<string, WorkflowPermissionStatus>
): string {
  const metadata = getPermissionActionMetadata(record.action)
  const conditions = normalizePermissionConditions(record.conditions)
  const values = [metadata.label]
  if (metadata.conditionKeys.includes('fieldIds')) {
    values.push(
      'Fields',
      ...conditions.fieldIds.map((id) =>
        permissionConditionName(id, fields, 'field')
      )
    )
  }
  if (
    metadata.conditionKeys.includes('approvalStatusId') &&
    conditions.approvalStatusId
  ) {
    values.push(
      'Approval Status',
      permissionConditionName(conditions.approvalStatusId, statuses, 'status')
    )
  }
  if (metadata.conditionKeys.includes('statusIds')) {
    values.push(
      'Statuses',
      ...conditions.statusIds.map((id) =>
        permissionConditionName(id, statuses, 'status')
      )
    )
  }
  return values.join(' ')
}

export function buildCreatePermissionInput(
  workflowId: string,
  values: PermissionFormValues
): CreatePermissionInput {
  const action = requireKnownAction(values.action)
  return {
    workflowId,
    action,
    conditions: buildConditions(action, formConditionValues(values)),
    users: idsFromLabeledValues(values.users),
  }
}

export function buildEditPermissionInput(
  record: WorkflowPermissionRecord,
  values: PermissionFormValues
): EditPermissionInput {
  const action = requireKnownAction(record.action)
  return {
    id: record.id,
    conditions: buildConditions(action, formConditionValues(values)),
    users: idsFromLabeledValues(values.users),
  }
}

export function buildEditPermissionInputFromRecord(
  record: WorkflowPermissionRecord,
  users: string[]
): EditPermissionInput {
  const action = requireKnownAction(record.action)
  return {
    id: record.id,
    conditions: buildConditions(
      action,
      normalizePermissionConditions(record.conditions)
    ),
    users,
  }
}

export function buildCreatePermissionInputFromRecord(
  record: WorkflowPermissionRecord
): CreatePermissionInput {
  const action = requireKnownAction(record.action)
  return {
    workflowId: record.workflowId,
    action,
    conditions: buildConditions(
      action,
      normalizePermissionConditions(record.conditions)
    ),
    users: [...record.users],
  }
}

function buildConditions(
  action: WorkflowPermissionAction,
  conditions: NormalizedPermissionConditions
): PermissionMutationConditions | null {
  if (action === 'field.edit') {
    const fieldEditConditions: PermissionMutationConditions = {
      type: action,
      fieldIds: conditions.fieldIds,
      statusIds: conditions.statusIds,
    }
    return fieldEditConditions
  } else if (
    action === 'approvals_status.advance' ||
    action === 'approvals_status.fast_track'
  ) {
    const approvalStatusConditions: PermissionMutationConditions = {
      type: action,
      approvalStatusId: conditions.approvalStatusId ?? '',
      statusIds: conditions.statusIds,
    }
    return approvalStatusConditions
  } else if (action === 'sharepoint-plugin.upload_remove') {
    const sharePointConditions: PermissionMutationConditions = {
      type: action,
      statusIds: conditions.statusIds,
    }
    return sharePointConditions
  } else if (action === 'workflow_record.revert') {
    const revertConditions: PermissionMutationConditions = {
      type: action,
      statusIds: conditions.statusIds,
    }
    return revertConditions
  } else if (
    action === 'workflow_record.create' ||
    action === 'workflow_record.remove_restore' ||
    action === 'comment.create' ||
    action === 'sharepoint-plugin.create_packet'
  ) {
    return null
  }
  throw new Error(`Unsupported permission action: ${action}`)
}

function formConditionValues(
  values: PermissionFormValues
): NormalizedPermissionConditions {
  return {
    approvalStatusId: values.approvalStatusId?.value.toString(),
    fieldIds: idsFromLabeledValues(values.fieldIds),
    statusIds: idsFromLabeledValues(values.statusIds),
  }
}

function idsFromLabeledValues(
  values: LabeledValueType[] | undefined
): string[] {
  return (values ?? []).map(({ value }) => value.toString())
}

function requireKnownAction(
  action: string | undefined
): WorkflowPermissionAction {
  if (isWorkflowPermissionAction(action)) return action
  throw new Error(`Unsupported permission action: ${action ?? 'none'}`)
}
